import React from "react";

import { getPathFromUrl } from "../shared/pageUtils";

/**
 * Dialog hook to show larger messages to the user.
 * Allows for customizability, incl. callbacks.
 *
 * Uses a queue-system so multiple dialogs won't overwrite each other.
 */
export function useDialog() {
    const [queue, setQueue] = React.useState([]);
    const [setupCompleted, setSetupCompleted] = React.useState(false);

    const [open, setOpen] = React.useState(false);
    const [title, setTitle] = React.useState("");
    const [message, setMessage] = React.useState("");

    const [actionColor, setActionColor] = React.useState("");
    const [actionTitle, setActionTitle] = React.useState("");

    const [cancelColor, setCancelColor] = React.useState("");
    const [cancelTitle, setCancelTitle] = React.useState("");

    const [minHeight, setMinHeight] = React.useState(null);
    const [component, setComponent] = React.useState(null);
    const [onClick, setOnClick] = React.useState(null);

    /**
     * Add a new dialog pop-up the queue.
     */
    const enqueueDialog = (options = {}) => {
        setQueue((prevQueue) => [...prevQueue, options]);
    };

    /**
     * Composite function to show a dialog pop-up along with the specified data.
     *
     * Don't show the dialog if the current path is the same as the hidePath.
     */
    const showDialog = (options = {}) => {
        const {
            title,
            message,
            actionTitle = "OK",
            actionColor = "secondary",
            cancelTitle = "Cancel",
            cancelColor = "primary"
        } = options;
        const { callback, component, minHeight, hidePath } = options;

        const path = getPathFromUrl();

        // Skip dialog, remove from queue.
        if (hidePath && path === hidePath) {
            setQueue((prevQueue) => prevQueue.slice(1));
            return;
        }

        // Content.
        setTitle(title);
        setMessage(message);
        setComponent(component);

        // Style.
        setActionTitle(actionTitle);
        setActionColor(actionColor);

        setCancelColor(cancelColor);
        setCancelTitle(cancelTitle);

        setMinHeight(minHeight);
        setOnClick(() => callback);
        setOpen(true);
    };

    // Show the next dialog from the queue.
    React.useEffect(() => {
        if (queue.length === 0) {
            setSetupCompleted(false);
        }

        if (queue.length > 0) {
            const options = queue[0];
            showDialog(options);
            setSetupCompleted(true);
        }
    }, [queue]);

    // Remove the first element from the dialog queue.
    React.useEffect(() => {
        if (!setupCompleted) return;

        if (queue.length > 0 && open === false) {
            setTimeout(() => {
                setQueue((prevQueue) => prevQueue.slice(1));
            }, 250);
        }
    }, [open]);

    return {
        // Style.
        title,
        message,
        actionColor,
        actionTitle,
        cancelColor,
        cancelTitle,
        component,
        minHeight,
        // Actions.
        open,
        setOpen,
        onClick,
        enqueueDialog
    };
}
